/**
 * FTS5-backed search index for scaffold files.
 *
 * Replaces the naive substring scan in searchScaffold() with BM25-ranked
 * full-text search via SQLite FTS5. Persists to .cai/.cache/search.sqlite
 * and rebuilds only when file mtimes change.
 *
 * Deterministic: no AI calls, no network. Synchronous (better-sqlite3).
 */
import Database from "better-sqlite3";
import { mkdirSync, statSync, existsSync } from "node:fs";
import { dirname } from "node:path";

export interface SearchResult {
  file: string;
  section: string;
  excerpt: string;
  rank: number;
}

interface MetaRow {
  key: string;
  value: string;
}

interface FtsRow {
  file: string;
  section: string;
  content: string;
  rank: number;
}

export class ScaffoldSearchIndex {
  private db: Database.Database;
  private ready = false;

  constructor(private dbPath: string) {
    mkdirSync(dirname(dbPath), { recursive: true });
    this.db = new Database(dbPath);
    this.db.pragma("journal_mode = WAL");
    this.db.pragma("synchronous = NORMAL");
    this._initSchema();
  }

  private _initSchema(): void {
    this.db.exec(`
      CREATE TABLE IF NOT EXISTS meta (
        key   TEXT PRIMARY KEY,
        value TEXT NOT NULL
      );
      CREATE VIRTUAL TABLE IF NOT EXISTS sections USING fts5(
        file,
        section,
        content,
        tokenize = 'porter ascii'
      );
    `);
    this.ready = true;
  }

  /**
   * Returns true if the index is missing or any file mtime has changed.
   * Cheap: only stats files, no disk reads.
   */
  isStale(files: string[]): boolean {
    if (!this.ready) return true;
    const stored = this._loadMtimes();
    if (stored.size === 0) return true;

    // Different set of files
    const storedKeys = new Set(stored.keys());
    const currentKeys = new Set(files);
    if (storedKeys.size !== currentKeys.size) return true;
    for (const f of currentKeys) {
      if (!storedKeys.has(f)) return true;
    }

    // Any mtime changed?
    for (const absPath of files) {
      try {
        const mtime = String(statSync(absPath).mtimeMs);
        if (stored.get(absPath) !== mtime) return true;
      } catch {
        return true;
      }
    }
    return false;
  }

  /**
   * Rebuild the index from scratch.
   * Called when isStale() returns true.
   */
  rebuild(files: Array<{ absPath: string; relPath: string; content: string }>): void {
    const insert = this.db.prepare(
      "INSERT INTO sections (file, section, content) VALUES (?, ?, ?)"
    );
    const setMeta = this.db.prepare(
      "INSERT OR REPLACE INTO meta (key, value) VALUES (?, ?)"
    );

    this.db.exec("DELETE FROM sections; DELETE FROM meta;");

    const run = this.db.transaction(() => {
      for (const { absPath, relPath, content } of files) {
        // Split into sections at heading boundaries for finer granularity
        const sectionChunks = splitSections(content);
        for (const { heading, body } of sectionChunks) {
          insert.run(relPath, heading, body);
        }
        try {
          const mtime = String(statSync(absPath).mtimeMs);
          setMeta.run(`mtime:${absPath}`, mtime);
        } catch {
          // file disappeared mid-run — skip
        }
      }
    });

    run();
  }

  /**
   * BM25-ranked search. Returns up to `limit` results.
   * FTS5 rank() is negative (lower = better match), so we negate for display.
   */
  search(query: string, limit = 5): SearchResult[] {
    if (!this.ready || !query.trim()) return [];

    // Sanitize: FTS5 is sensitive to unmatched quotes and operators
    const safeQuery = sanitizeFtsQuery(query);
    if (!safeQuery) return [];

    try {
      const rows = this.db
        .prepare(
          `SELECT file, section, content, rank
           FROM sections
           WHERE sections MATCH ?
           ORDER BY rank
           LIMIT ?`
        )
        .all(safeQuery, limit) as FtsRow[];

      return rows.map((r) => ({
        file: r.file,
        section: r.section,
        excerpt: excerpt(r.content, safeQuery),
        rank: -r.rank, // FTS5 rank is negative BM25; flip for readability
      }));
    } catch {
      // Malformed query after sanitization — return empty rather than crash
      return [];
    }
  }

  close(): void {
    this.db.close();
  }

  private _loadMtimes(): Map<string, string> {
    const rows = this.db
      .prepare("SELECT key, value FROM meta WHERE key LIKE 'mtime:%'")
      .all() as MetaRow[];
    const map = new Map<string, string>();
    for (const { key, value } of rows) {
      map.set(key.slice("mtime:".length), value);
    }
    return map;
  }
}

/** Split markdown into {heading, body} chunks at heading boundaries. */
function splitSections(content: string): Array<{ heading: string; body: string }> {
  const lines = content.split("\n");
  const chunks: Array<{ heading: string; body: string }> = [];
  let currentHeading = "(preamble)";
  let currentLines: string[] = [];

  function flush() {
    const body = currentLines.join("\n").trim();
    if (body.length > 0) {
      chunks.push({ heading: currentHeading, body });
    }
  }

  for (const line of lines) {
    const m = line.match(/^(#{1,6})\s+(.+)/);
    if (m) {
      flush();
      currentHeading = m[2].trim();
      currentLines = [];
    } else {
      currentLines.push(line);
    }
  }
  flush();

  return chunks;
}

/** Extract a ~3-line excerpt centred on the first query term match. */
function excerpt(content: string, query: string): string {
  const lines = content.split("\n");
  const term = query.split(/\s+/)[0].toLowerCase().replace(/[*"]/g, "");
  const idx = lines.findIndex((l) => l.toLowerCase().includes(term));
  if (idx === -1) return lines.slice(0, 3).join("\n").trim();
  const start = Math.max(0, idx - 1);
  const end = Math.min(lines.length - 1, idx + 2);
  return lines.slice(start, end + 1).join("\n").trim();
}

/**
 * Sanitize a free-text query for FTS5 MATCH.
 * FTS5 treats unmatched quotes and bare operators as syntax errors.
 * Strategy: remove special chars, wrap each token in double-quotes,
 * join with implicit AND (space).
 */
function sanitizeFtsQuery(query: string): string {
  const tokens = query
    .toLowerCase()
    .replace(/[^\w\s-]/g, " ")
    .trim()
    .split(/\s+/)
    .filter((t) => t.length > 1);

  if (tokens.length === 0) return "";
  // Wrap in quotes so FTS5 treats each as a phrase prefix
  return tokens.map((t) => `"${t}"`).join(" ");
}

/** Check if better-sqlite3 native module loaded correctly. */
export function isSqliteAvailable(): boolean {
  try {
    const db = new Database(":memory:");
    db.close();
    return true;
  } catch {
    return false;
  }
}
